"use client"

import { useEffect, useState } from "react"
import { useRecentlyViewed } from "@/hooks/use-recently-viewed"
import LocalizedClientLink from "@modules/common/components/localized-client-link"
import Thumbnail from "@modules/products/components/thumbnail"
import { useTranslations, useLocale } from "next-intl"
import { HttpTypes } from "@medusajs/types"
import { listProducts } from "@lib/data/products"
import { getLocalizedProductContent } from "@lib/util/product-localization"
import type { Locale } from "@/types/i18n"

interface RecentlyViewedProps {
  countryCode: string
  currentHandle?: string
}

export default function RecentlyViewed({
  countryCode,
  currentHandle,
}: RecentlyViewedProps) {
  const { recentlyViewed } = useRecentlyViewed()
  const t = useTranslations("home.recentlyViewed")
  const locale = useLocale() as Locale
  const [products, setProducts] = useState<HttpTypes.StoreProduct[]>([])
  const [isLoading, setIsLoading] = useState(false)

  // Filter out the current product page
  const handles = recentlyViewed.filter((h) => h !== currentHandle)

  useEffect(() => {
    if (handles.length === 0) {
      setProducts([])
      return
    }

    let cancelled = false
    setIsLoading(true)

    // Fetch products by handles
    const fetchProducts = async () => {
      try {
        const batch = handles.slice(0, 6)
        const results = await listProducts({
          countryCode,
          queryParams: { handle: batch, limit: 6 },
        })
          .then(({ response }) => response.products)
          .catch(() => [])

        if (!cancelled) {
          // Re-sort to match original handles order
          const ordered = batch
            .map((h) => results.find((p) => p.handle === h))
            .filter(Boolean) as HttpTypes.StoreProduct[]
          setProducts(ordered)
        }
      } catch {
        // Silently fail - recently viewed is non-critical
      } finally {
        if (!cancelled) {
          setIsLoading(false)
        }
      }
    }

    fetchProducts()
    return () => {
      cancelled = true
    }
  }, [handles.join(","), countryCode])

  // Don't render if nothing to show
  if (handles.length === 0 || (!isLoading && products.length === 0)) {
    return null
  }

  return (
    <div className="w-full">
      <h2 className="text-2xl font-semibold mb-4">{t("title")}</h2>
      <div className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide">
        {products.map((product) => {
          const localized = getLocalizedProductContent(product, locale)
          return (
            <LocalizedClientLink
              key={product.id}
              href={`/products/${product.handle}`}
              className="shrink-0 w-40 sm:w-48 group"
            >
              <Thumbnail
                thumbnail={product.thumbnail}
                images={product.images}
                size="square"
                alt={localized.title || "Product"}
              />
              <p className="text-sm mt-2 text-foreground line-clamp-2 group-hover:underline">
                {localized.title}
              </p>
            </LocalizedClientLink>
          )
        })}
      </div>
    </div>
  )
}
